/**
 * Statement Recorder - Bank/Cash Balance Recording
 * Manual input form with image upload for bank statement screenshots
 */

import React, { useState, useRef, useEffect } from 'react';
import { Company, Transaction } from '../../types';
import { Card, Button, Input, Select } from '../UI';
import { useTitle, PAGE_TITLES } from '../../hooks';
import { uploadsApi, transactionsApi, UploadedFile } from '../../services/api.service';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Check, 
  Building, 
  Upload, 
  Image as ImageIcon,
  Trash2,
  FileText, 
  X,
  Landmark,
  Calendar,
  Eye,
  Wallet,
  ArrowRight,
  Loader2,
  Edit3,
  AlertTriangle,
  RefreshCw,
  ArrowUpCircle,
  ArrowDownCircle
} from 'lucide-react';

// Currency symbol mapping
const CURRENCY_SYMBOLS: Record<string, string> = {
  'IDR': 'Rp',
  'USD': '$',
  'EUR': '€',
  'SGD': 'S$',
  'JPY': '¥',
  'GBP': '£',
  'AUD': 'A$',
  'CNY': '¥',
  'MYR': 'RM',
  'THB': '฿',
};

interface UploadedImage {
  id: string;
  name: string;
  previewUrl: string;
  uploadedAt: Date;
  companyId: string;
  bankAccountId?: string;
}

interface RejectedTransaction {
  transaction_id: string;
  company_id: string;
  account_id: string | null;
  user_id: string | null;
  date: string;
  description: string;
  amount: number;
  type: 'CREDIT' | 'DEBIT';
  status: string;
  imageUrl: string | null;
  createdAt: string;
  company?: { name: string };
  bankAccount?: { bankName: string; accountNumber: string };
}

interface StatementRecorderProps {
  companies: Company[];
  selectedCompanyId: string;
  onSelectCompany: (id: string) => void;
  onSave: (transaction: Transaction) => void;
}

export const StatementRecorder: React.FC<StatementRecorderProps> = ({ 
  companies, 
  selectedCompanyId, 
  onSelectCompany,
  onSave
}) => {
  useTitle(PAGE_TITLES.STATEMENT_RECORDER);
  const { user } = useAuth();

  // Manual Form State
  const [formData, setFormData] = useState({
    date: '',
    description: '',
    amount: '',
    type: 'debit' as 'credit' | 'debit',
    bankAccountId: ''
  });

  // Upload State
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [previewImage, setPreviewImage] = useState<UploadedImage | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Rejected transactions state
  const [rejectedTransactions, setRejectedTransactions] = useState<RejectedTransaction[]>([]);
  const [isLoadingRejected, setIsLoadingRejected] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<RejectedTransaction | null>(null);
  const [editFormData, setEditFormData] = useState({
    date: '',
    description: '',
    amount: '',
    type: 'debit' as 'credit' | 'debit',
    bankAccountId: ''
  });
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [editImageUrl, setEditImageUrl] = useState<string | null>(null);
  const [isUploadingEditImage, setIsUploadingEditImage] = useState(false);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-select first company on mount
  useEffect(() => {
    if (!selectedCompanyId && companies.length > 0) {
      onSelectCompany(companies[0].id);
    }
  }, [companies, selectedCompanyId, onSelectCompany]);

  // Reset bank account when company changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, bankAccountId: '' }));
  }, [selectedCompanyId]);

  // Fetch rejected transactions for current user (only STATEMENT source)
  const fetchRejectedTransactions = async () => {
    if (!user) return;
    
    setIsLoadingRejected(true);
    try {
      const response = await transactionsApi.getAll({ status: 'FLAGGED', user_id: user.user_id, source: 'STATEMENT' });
      const data = response?.data || [];
      setRejectedTransactions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch rejected transactions:', err);
    } finally {
      setIsLoadingRejected(false);
    }
  };

  useEffect(() => {
    fetchRejectedTransactions();
  }, [user]);

  // Handle edit transaction
  const handleEditClick = (tx: RejectedTransaction) => {
    setEditingTransaction(tx);
    setEditFormData({
      date: tx.date.split('T')[0],
      description: tx.description,
      amount: tx.amount.toString(),
      type: tx.type.toLowerCase() as 'credit' | 'debit',
      bankAccountId: tx.account_id || ''
    });
    setEditImageUrl(tx.imageUrl); // Set current image
  };

  // Handle image upload for edit modal
  const handleEditImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!file.type.startsWith('image/')) {
      alert('Hanya file gambar yang didukung.');
      return;
    }

    setIsUploadingEditImage(true);
    try {
      const uploadedFiles = await uploadsApi.upload('statements', [file]);
      if (uploadedFiles.length > 0) {
        setEditImageUrl(uploadedFiles[0].url);
      }
    } catch (err) {
      console.error('Failed to upload image:', err);
      alert('Gagal mengunggah gambar.');
    } finally {
      setIsUploadingEditImage(false);
      if (editFileInputRef.current) {
        editFileInputRef.current.value = '';
      }
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTransaction) return;

    setIsSubmittingEdit(true);
    try {
      await transactionsApi.update(editingTransaction.transaction_id, {
        date: editFormData.date,
        description: editFormData.description,
        amount: parseFloat(editFormData.amount),
        type: editFormData.type.toUpperCase() as 'CREDIT' | 'DEBIT',
        bankAccountId: editFormData.bankAccountId || undefined,
        status: 'PENDING', // Resubmit as pending
        imageUrl: editImageUrl || undefined
      });
      showSuccess('Transaksi berhasil diperbarui dan diajukan ulang!');
      setEditingTransaction(null);
      setEditImageUrl(null);
      fetchRejectedTransactions(); // Refresh list
    } catch (err) {
      console.error('Failed to update transaction:', err);
      alert('Gagal memperbarui transaksi. Silakan coba lagi.');
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  // Group companies for dropdown
  const getCompanyOptions = () => {
    const holdings = companies.filter(c => c.isHolding);
    const independents = companies.filter(c => !c.parentId && !c.isHolding);
    
    const renderOption = (c: Company, depth: number = 0) => (
      <option key={c.id} value={c.id}>
         {'\u00A0'.repeat(depth * 4)} {depth > 0 ? '↳ ' : ''}{c.name}
      </option>
    );

    let options: React.ReactNode[] = [];
    holdings.forEach(holding => {
        options.push(renderOption(holding));
        const children = companies.filter(c => c.parentId === holding.id);
        children.forEach(child => options.push(renderOption(child, 1)));
    });
    if (independents.length > 0) {
        if (holdings.length > 0) options.push(<option key="div" disabled>──────────</option>);
        independents.forEach(ind => options.push(renderOption(ind)));
    }
    return options;
  };

  const getCompanyAccounts = (companyId: string) => {
    // Only return BANK type accounts for Statement Recorder (exclude INVESTMENT)
    const allAccounts = companies.find(c => c.id === companyId)?.bankAccounts || [];
    // Filter out INVESTMENT accounts - only show BANK accounts for statements
    // Default to BANK if accountType is not set
    return allAccounts.filter(account => !account.accountType || account.accountType === 'BANK');
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFiles = async (files: File[]) => {
    if (!selectedCompanyId) {
      alert('Silakan pilih perusahaan terlebih dahulu.');
      return;
    }

    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      alert('Hanya file gambar yang didukung (PNG, JPG).');
      return;
    }

    setIsUploading(true);
    
    try {
      // Upload to backend
      const uploadedFiles = await uploadsApi.upload('statements', imageFiles);
      
      // Create local preview entries
      const newImages: UploadedImage[] = uploadedFiles.map(file => ({
        id: file.fileName,
        name: file.originalName,
        previewUrl: file.url,
        uploadedAt: new Date(file.uploadedAt),
        companyId: selectedCompanyId,
        bankAccountId: formData.bankAccountId || undefined
      }));
      
      setUploadedImages(prev => [...newImages, ...prev]);
      showSuccess(`${imageFiles.length} gambar berhasil diunggah!`);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Gagal mengunggah gambar. Silakan coba lagi.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(Array.from(files));
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteImage = (id: string) => {
    setUploadedImages(prev => prev.filter(img => img.id !== id));
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCompanyId) {
      alert("Silakan pilih perusahaan terlebih dahulu.");
      return;
    }
    
    const amountNum = parseFloat(formData.amount);
    if (isNaN(amountNum)) return;

    // Get the first uploaded image URL (if any)
    const imageUrl = uploadedImages.length > 0 ? uploadedImages[0].previewUrl : undefined;

    onSave({
      id: `t${Date.now()}`,
      companyId: selectedCompanyId,
      bankAccountId: formData.bankAccountId || undefined,
      date: formData.date,
      description: formData.description,
      amount: amountNum,
      type: formData.type,
      status: 'pending',
      imageUrl
    });

    showSuccess('Data berhasil direkam!');
    setFormData({ date: '', description: '', amount: '', type: 'debit', bankAccountId: formData.bankAccountId });
    // Clear uploaded images after saving
    setUploadedImages([]);
  };

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const getCompanyName = (companyId: string) => {
    return companies.find(c => c.id === companyId)?.name || 'Unknown';
  };

  const getBankAccountName = (companyId: string, bankAccountId?: string) => {
    if (!bankAccountId) return 'Kas';
    const company = companies.find(c => c.id === companyId);
    const account = company?.bankAccounts?.find(b => b.id === bankAccountId);
    return account ? `${account.bankName} - ${account.accountNumber}` : 'Unknown';
  };

  const activeCompanyAccounts = getCompanyAccounts(selectedCompanyId);

  // Get currency symbol for the selected bank account
  const selectedCurrency = React.useMemo(() => {
    if (!formData.bankAccountId) return { code: 'IDR', symbol: 'Rp' };
    const account = activeCompanyAccounts.find(a => a.id === formData.bankAccountId);
    const code = account?.currency || 'IDR';
    return { code, symbol: CURRENCY_SYMBOLS[code] || code };
  }, [formData.bankAccountId, activeCompanyAccounts]);

  return (
    <div className="space-y-8">
      
      {/* Success Notification */}
      {successMessage && (
        <div className="fixed top-24 right-8 z-50 animate-fade-in-down">
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-xl shadow-lg flex items-center">
            <div className="bg-emerald-100 rounded-full p-1 mr-3">
                <Check className="w-4 h-4 text-emerald-600" />
            </div>
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left Column: Manual Entry Form - shows second on mobile, first on desktop */}
        <div className="lg:col-span-7 space-y-6 order-2 lg:order-1">
          <Card className="p-6 sm:p-8 h-full">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Input Manual</h2>
                <p className="text-sm text-gray-500 mt-1">Catat saldo bank/kas harian ke buku besar.</p>
              </div>
            </div>

            <form onSubmit={handleManualSubmit}>
              <Select
                label="Nama Perusahaan"
                startIcon={Building}
                value={selectedCompanyId}
                onChange={(e) => onSelectCompany(e.target.value)}
              >
                <option value="" disabled>Pilih perusahaan...</option>
                {getCompanyOptions()}
              </Select>

              <Select
                label="Rekening Bank"
                startIcon={Landmark}
                value={formData.bankAccountId}
                onChange={(e) => setFormData({...formData, bankAccountId: e.target.value})}
                disabled={!selectedCompanyId}
              >
                <option value="">Kas (Tanpa rekening khusus)</option>
                {activeCompanyAccounts.map(acc => (
                  <option key={acc.id} value={acc.id}>{acc.bankName} - {acc.accountNumber}</option>
                ))}
              </Select>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5">
                <Input
                  label="Tanggal Saldo"
                  type="date"
                  startIcon={Calendar}
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                />
                <Select
                  label="Jenis Transaksi"
                  startIcon={Wallet}
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value as any})}
                >
                  <option value="debit">Debit</option>
                  <option value="credit">Kredit</option>
                </Select>
              </div>

              <div className="w-full sm:w-1/2">
                <Input
                  label={`Saldo Akhir Hari (${selectedCurrency.code})`}
                  type="number"
                  step="0.01"
                  min="0"
                  startText={selectedCurrency.symbol}
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  required
                  className="font-mono"
                />
              </div>

              <Input
                label="Keterangan"
                startIcon={FileText}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                required
              />

              <div className="pt-2">
                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full"
                  disabled={!selectedCompanyId || !formData.date || !formData.amount || !formData.description || uploadedImages.length === 0}
                >
                  Simpan Data <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </form>
          </Card>
        </div>

        {/* Right Column: Upload Area - shows first on mobile, second on desktop */}
        <div className="lg:col-span-5 space-y-6 order-1 lg:order-2">
          <div className="bg-linear-to-br from-indigo-600 to-violet-700 rounded-2xl p-5 text-white shadow-xl shadow-indigo-200 flex flex-col">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm">
                <FileText className="w-5 h-5 text-yellow-300" />
              </div>
              <div>
                <h3 className="font-bold text-base">Unggah Screenshot</h3>
              </div>
            </div>
            
            <p className="text-sm text-indigo-50 mb-4 leading-relaxed opacity-90">
              Geser dan letakkan gambar tangkapan layar saldo bank/kas Anda di sini.
            </p>
            
            {/* Drag & Drop Area */}
            <div 
              className="relative"
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input 
                 type="file" 
                 ref={fileInputRef}
                 className="hidden" 
                 accept="image/*"
                 multiple
                 onChange={handleFileUpload}
              />
              
              <div 
                 onClick={() => fileInputRef.current?.click()}
                 className={`
                   flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-200
                   ${dragActive 
                       ? 'border-yellow-400 bg-white/20 scale-105' 
                       : 'border-indigo-300/30 bg-indigo-800/20 hover:bg-indigo-800/30'}
                 `}
              >
                 <Upload className={`w-10 h-10 mx-auto mb-3 transition-colors ${dragActive ? 'text-yellow-300' : 'text-indigo-300'}`} />
                 <p className="text-sm text-white font-medium">Klik atau Geser Gambar ke Sini</p>
                 <p className="text-xs text-indigo-300 mt-1">Mendukung PNG, JPG</p>
              </div>
            </div>

            {/* Uploaded Images Gallery - inside upload card */}
            {uploadedImages.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/20">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-white/90 flex items-center">
                    <div className="w-2 h-2 rounded-full bg-yellow-400 mr-2"></div>
                    Terunggah ({uploadedImages.length})
                  </h4>
                  <button 
                    onClick={() => {
                      if (confirm('Hapus semua gambar?')) {
                        setUploadedImages([]);
                      }
                    }}
                    className="text-xs text-white/60 hover:text-red-300 transition-colors"
                  >
                    Hapus Semua
                  </button>
                </div>
                
                <div className="grid grid-cols-4 gap-2">
                  {uploadedImages.map((img) => (
                    <div 
                      key={img.id} 
                      className="group relative bg-white/10 rounded-lg overflow-hidden"
                    >
                      {/* Image Preview */}
                      <div 
                        className="aspect-square cursor-pointer"
                        onClick={() => setPreviewImage(img)}
                      >
                        <img 
                          src={img.previewUrl} 
                          alt={img.name}
                          className="w-full h-full object-cover"
                        />
                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Eye className="w-4 h-4 text-white" />
                        </div>
                      </div>

                      {/* Delete Button */}
                      <button 
                        onClick={() => handleDeleteImage(img.id)}
                        className="absolute top-0.5 right-0.5 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      >
                        <Trash2 className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="fixed inset-0 bg-black/80"
            onClick={() => setPreviewImage(null)}
          />
          <div className="relative max-w-4xl max-h-[90vh] overflow-auto bg-white rounded-2xl shadow-2xl">
            <button 
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 z-10"
            >
              <X className="w-5 h-5" />
            </button>
            <img 
              src={previewImage.previewUrl} 
              alt={previewImage.name}
              className="max-w-full max-h-[80vh] object-contain"
            />
            <div className="p-4 border-t">
              <p className="font-medium text-gray-900">{previewImage.name}</p>
              <p className="text-sm text-gray-500">
                {getCompanyName(previewImage.companyId)} • {getBankAccountName(previewImage.companyId, previewImage.bankAccountId)}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Diunggah: {previewImage.uploadedAt.toLocaleString('id-ID')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Rejected Transactions Table */}
      {rejectedTransactions.length > 0 && (
        <Card className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900">Transaksi Ditolak</h3>
                <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">Transaksi yang perlu diperbaiki dan diajukan ulang</p>
              </div>
            </div>
            <button
              onClick={fetchRejectedTransactions}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors touch-target"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${isLoadingRejected ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Mobile Card Layout */}
          <div className="sm:hidden space-y-3">
            {rejectedTransactions.map(tx => (
              <div key={tx.transaction_id} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-gray-500">
                        {new Date(tx.date).toLocaleDateString('id-ID')}
                      </span>
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                        tx.type === 'CREDIT' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {tx.type === 'CREDIT' ? <ArrowUpCircle className="w-3 h-3" /> : <ArrowDownCircle className="w-3 h-3" />}
                        {tx.type === 'CREDIT' ? 'Kredit' : 'Debit'}
                      </span>
                    </div>
                    <p className="font-semibold text-gray-900 text-sm truncate">{tx.company?.name || '-'}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {tx.bankAccount ? `${tx.bankAccount.bankName} - ${tx.bankAccount.accountNumber}` : 'Kas'}
                    </p>
                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">{tx.description}</p>
                    <p className={`font-bold mt-2 ${tx.type === 'CREDIT' ? 'text-green-600' : 'text-red-600'}`}>
                      {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(tx.amount)}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {tx.imageUrl ? (
                      <a href={tx.imageUrl} target="_blank" rel="noopener noreferrer">
                        <img 
                          src={tx.imageUrl} 
                          alt="Bukti" 
                          className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                        />
                      </a>
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        <ImageIcon className="w-5 h-5 text-gray-400" />
                      </div>
                    )}
                    <button
                      onClick={() => handleEditClick(tx)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors font-medium text-xs touch-target"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table Layout */}
          <div className="hidden sm:block overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Tanggal</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Perusahaan</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Rekening</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Deskripsi</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-700">Gambar</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Jumlah</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-700">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rejectedTransactions.map(tx => (
                  <tr key={tx.transaction_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-600">
                      {new Date(tx.date).toLocaleDateString('id-ID')}
                    </td>
                    <td className="px-4 py-3 text-gray-900 font-medium">
                      {tx.company?.name || '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {tx.bankAccount ? `${tx.bankAccount.bankName} - ${tx.bankAccount.accountNumber}` : 'Kas'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs truncate">
                      {tx.description}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {tx.imageUrl ? (
                        <a href={tx.imageUrl} target="_blank" rel="noopener noreferrer">
                          <img 
                            src={tx.imageUrl} 
                            alt="Bukti" 
                            className="w-10 h-10 object-cover rounded-lg border border-gray-200 hover:border-blue-400 transition-colors cursor-pointer mx-auto"
                          />
                        </a>
                      ) : (
                        <div className="w-10 h-10 mx-auto bg-gray-100 rounded-lg flex items-center justify-center">
                          <ImageIcon className="w-4 h-4 text-gray-400" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className={`flex items-center justify-end gap-1 font-medium ${
                        tx.type === 'CREDIT' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {tx.type === 'CREDIT' ? (
                          <ArrowUpCircle className="w-4 h-4" />
                        ) : (
                          <ArrowDownCircle className="w-4 h-4" />
                        )}
                        {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(tx.amount)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleEditClick(tx)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors font-medium text-xs"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Edit Transaction Modal */}
      {editingTransaction && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setEditingTransaction(null)}
          />
          <div className="relative bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl animate-slideUp sm:animate-scaleIn max-h-[90vh] sm:max-h-[85vh] flex flex-col">
            <div className="border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between shrink-0">
              {/* Mobile drag indicator */}
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-full sm:hidden" />
              <div className="pt-2 sm:pt-0">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">Edit Transaksi</h3>
                <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">Perbaiki dan ajukan ulang transaksi</p>
              </div>
              <button 
                onClick={() => setEditingTransaction(null)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors touch-target"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              {/* Image Upload/Replace */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bukti Transaksi</label>
                <input
                  type="file"
                  ref={editFileInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={handleEditImageUpload}
                />
                {editImageUrl ? (
                  <div className="relative group">
                    <img 
                      src={editImageUrl} 
                      alt="Bukti Transaksi" 
                      className="w-full max-h-40 sm:max-h-48 object-contain rounded-xl border border-gray-200 bg-gray-50"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl items-center justify-center gap-2 hidden sm:flex">
                      <button
                        type="button"
                        onClick={() => editFileInputRef.current?.click()}
                        disabled={isUploadingEditImage}
                        className="px-3 py-1.5 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
                      >
                        {isUploadingEditImage ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Ganti Gambar'
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditImageUrl(null)}
                        className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors"
                      >
                        Hapus
                      </button>
                    </div>
                    {/* Mobile action buttons */}
                    <div className="flex gap-2 mt-2 sm:hidden">
                      <button
                        type="button"
                        onClick={() => editFileInputRef.current?.click()}
                        disabled={isUploadingEditImage}
                        className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium touch-target"
                      >
                        {isUploadingEditImage ? (
                          <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                        ) : (
                          'Ganti Gambar'
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditImageUrl(null)}
                        className="flex-1 py-2 bg-red-50 text-red-600 rounded-lg text-sm font-medium touch-target"
                      >
                        Hapus
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => editFileInputRef.current?.click()}
                    disabled={isUploadingEditImage}
                    className="w-full h-28 sm:h-32 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-blue-400 hover:bg-blue-50 transition-colors touch-target"
                  >
                    {isUploadingEditImage ? (
                      <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-6 h-6 text-gray-400" />
                        <span className="text-sm text-gray-500">Klik untuk unggah gambar</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              <Input
                label="Tanggal"
                type="date"
                startIcon={Calendar}
                value={editFormData.date}
                onChange={(e) => setEditFormData({...editFormData, date: e.target.value})}
                required
              />

              <Select
                label="Rekening Bank"
                startIcon={Landmark}
                value={editFormData.bankAccountId}
                onChange={(e) => setEditFormData({...editFormData, bankAccountId: e.target.value})}
              >
                <option value="">Kas (Tanpa rekening khusus)</option>
                {companies
                  .find(c => c.id === editingTransaction.company_id)
                  ?.bankAccounts?.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.bankName} - {acc.accountNumber}</option>
                  ))}
              </Select>

              <Select
                label="Jenis Transaksi"
                startIcon={Wallet}
                value={editFormData.type}
                onChange={(e) => setEditFormData({...editFormData, type: e.target.value as any})}
              >
                <option value="debit">Debit</option>
                <option value="credit">Kredit</option>
              </Select>

              <Input
                label={`Jumlah (${selectedCurrency.code})`}
                type="number"
                step="0.01"
                min="0"
                startText={selectedCurrency.symbol}
                value={editFormData.amount}
                onChange={(e) => setEditFormData({...editFormData, amount: e.target.value})}
                required
              />

              <Input
                label="Deskripsi"
                type="text"
                startIcon={FileText}
                value={editFormData.description}
                onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                required
              />
            </form>

            {/* Fixed bottom buttons on mobile */}
            <div className="flex gap-3 p-4 sm:px-6 sm:pb-6 border-t sm:border-t-0 border-gray-100 shrink-0 pb-safe">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setEditingTransaction(null)}
                className="flex-1"
                fullWidthMobile
              >
                Batal
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmittingEdit}
                className="flex-1"
                fullWidthMobile
                onClick={handleEditSubmit}
              >
                {isSubmittingEdit ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Ajukan Ulang
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
